import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, CreditCard, Mail, Calendar, Crown, Shield, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { supabase } from '../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [managingSubscription, setManagingSubscription] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      const [userRes, subRes] = await Promise.all([
        axios.get(`${API}/api/auth/me`, { withCredentials: true }),
        axios.get(`${API}/api/subscription/status`, { withCredentials: true })
      ]);
      
      setUser(userRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      if (error.response?.status === 401) {
        navigate('/auth');
      } else {
        toast.error('Failed to load account data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      
      const res = await fetch(`${API}/api/subscriptions/create-portal-session`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to open billing portal');
      }
      
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to open billing portal');
      setManagingSubscription(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFD] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  const isAdmin = subscription?.isAdmin || user?.role === 'admin';
  const isPro = subscription?.currentPlan?.toLowerCase() === 'pro';
  const isBeta = subscription?.currentPlan?.toLowerCase() === 'beta';
  const isBasic = subscription?.currentPlan?.toLowerCase() === 'basic';
  const hasProFeatures = isPro || isBeta; // Beta has Pro-level features
  const hasSubscription = subscription?.subscriptionActive;

  return (
    <div className="min-h-screen bg-[#F8FAFD]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1E3A5C] to-[#2D5A87] rounded-lg">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">My Account</h1>
              <p className="text-slate-600">Manage your profile and subscription</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              Profile Information
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <span className="text-slate-600">Email</span>
              </div>
              <span className="text-slate-900 font-medium">{user?.email || 'Not available'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <span className="text-slate-600">Name</span>
              </div>
              <span className="text-slate-900 font-medium">{user?.full_name || user?.email?.split('@')[0] || 'Not set'}</span>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600">Role</span>
                </div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  <Shield className="w-4 h-4" />
                  Administrator
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-slate-600" />
              Subscription
            </h2>
          </div>
          <div className="p-6">
            {isAdmin ? (
              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-900">Administrator Access</h3>
                  <p className="text-purple-700 text-sm">You have full access to all features as an admin.</p>
                </div>
              </div>
            ) : hasSubscription ? (
              <div className="space-y-4">
                <div className={`flex items-center gap-4 p-4 rounded-xl border ${
                  hasProFeatures 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    hasProFeatures ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    {hasProFeatures ? (
                      <Crown className="w-6 h-6 text-amber-600" />
                    ) : (
                      <User className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${hasProFeatures ? 'text-amber-900' : 'text-blue-900'}`}>
                      {subscription.currentPlan} Plan
                    </h3>
                    <p className={`text-sm ${hasProFeatures ? 'text-amber-700' : 'text-blue-700'}`}>>
                      {hasProFeatures ? 'Full access to all features' : 'Access to quizzes and 3 case studies/month'}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    Active
                  </span>
                </div>

                {isBasic && (
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-slate-700 font-medium">Case Studies Usage</p>
                      <p className="text-slate-600 text-sm">
                        You have used <strong>{subscription.caseStudiesUsed || 0}</strong> of <strong>3</strong> case studies this month.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    className="bg-[#1E3A5C] hover:bg-[#264B73]"
                  >
                    {managingSubscription ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Manage Subscription
                      </>
                    )}
                  </Button>
                  {isBasic && (
                    <Button
                      onClick={() => navigate('/pricing')}
                      variant="outline"
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </Button>
                  )}
                </div>
                <p className="text-slate-500 text-sm">
                  Manage billing, update payment method, or cancel your subscription through the Stripe portal.
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">No Active Subscription</h3>
                <p className="text-slate-600 mb-6">Subscribe to access quizzes, case studies, and games.</p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-[#FF9E00] hover:bg-[#E88D00]"
                >
                  View Pricing Plans
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-slate-900">Account Actions</h2>
          </div>
          <div className="p-6">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
